import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogTitle } from '@mui/material';

import {
    loadActionPlanStatusDetails,
    loadBarrierNameDetails
} from '../../../store/directusService';
import {
    loadActionPlans,
    loadContractConstants,
    loadParticipantBarriers
} from '../../../store/participantService';
import { getNameFromId } from '../../../utils/directusFunctions';
import { DEFAULT_TABLE_ROWS_PER_PAGE, EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import ActionPlanTableRow from './ActionPlanTableRow';
import CreateActionPlan from './CreateActionPlan';
import EditActionPlan from './EditActionPlan';
import GenerateActionPlanPDF from './GenerateActionPlanPDF';

import app from '../../../app.module.css';
import classes from './actionPlan.module.css';

const headCells = [
    { id: 'actionTitle', numeric: false, label: 'Action Name' },
    { id: 'barrierName', numeric: false, label: 'Barrier' },
    { id: 'actionStartDate', numeric: false, label: 'Start Date' },
    { id: 'actionDueDate', numeric: true, label: 'Review Date' },
    { id: 'actionOutcomeDate', numeric: true, label: 'Outcome Date' },
    { id: 'outcomeName', numeric: true, label: 'Outcome' }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'actionStartDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const ActionPlan = ({ isViewOnly = false }) => {
    const dispatch = useDispatch();
    const contentTemplateContainerTemplateRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [openEditor, setOpenEditor] = useState(false);
    const [formType, setFormType] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);

    // STORE STATE
    const barrierNameDetails = useSelector(
        (state) => state.entities.directusService.barrierNameDetails
    );
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const participantActionPlans = useSelector(
        (state) => state.entities.participantService.participantActionPlans
    );
    const currentParticipantBarriers = useSelector(
        (state) => state.entities.participantService.currentParticipantBarriers
    );
    const actionPlanStatuses = useSelector(
        (state) => state.entities.directusService.actionPlanStatusDetails
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );

    // USE EFFECTS
    useEffect(() => {
        setRows([]);
        barrierNameDetails.length < 1 && dispatch(loadBarrierNameDetails());
        actionPlanStatuses?.length < 1 && dispatch(loadActionPlanStatusDetails());
    }, []);

    useEffect(() => {
        if (!('id' in currentParticipant) || roles.length < 1) return;
        const disableOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus || isViewOnly);
    }, [currentParticipant, roles, isViewOnly]);

    useEffect(() => {
        if (!('id' in currentParticipant)) return;
        dispatch(loadActionPlans(currentParticipant.id));
        if (contractConstants?.contractId !== currentParticipant.contractId)
            dispatch(loadContractConstants(currentParticipant.contractId));
        if (
            !currentParticipantBarriers?.length ||
            currentParticipantBarriers?.participantId !== currentParticipant.id
        )
            dispatch(loadParticipantBarriers(currentParticipant.id));
    }, [currentParticipant]);

    useEffect(() => {
        if (actionPlanStatuses?.length < 1 || barrierNameDetails?.length < 1) return;
        const rows = participantActionPlans?.map((el) => ({
            ...el,
            actionStartDate: !el.actionStartDate ? '1960-01-01' : el.actionStartDate,
            actionDueDate: !el.actionDueDate ? '1960-01-01' : el.actionDueDate,
            actionOutcomeDate: !el.actionOutcomeDate ? '1960-01-01' : el.actionOutcomeDate,
            outcomeName: getNameFromId(actionPlanStatuses, el.outcomeId) || 'zz',
            barrierName:
                getNameFromId(
                    barrierNameDetails,
                    currentParticipantBarriers?.find((entry) => entry.id === el.barrierId)
                        ?.barrierId
                ) || 'zz'
        }));
        setRows(rows);
    }, [
        participantActionPlans,
        actionPlanStatuses,
        currentParticipantBarriers,
        barrierNameDetails
    ]);

    // HELPER fNS
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <ActionPlanTableRow
                key={el.id}
                row={el}
                isDisabled={isDisabled}
                onEdit={onEditAction}
            />
        ));

    // EVENT HANDLERS
    const onCreateAction = () => {
        setFormType('create');
        setOpenEditor(true);
    };

    const onEditAction = (row) => {
        setSelectedRow(row);
        setFormType('edit');
        setOpenEditor(true);
    };

    const onEditorClose = () => setOpenEditor(false);

    const onClosePdfPreview = () => {
        setShowPdfPreview(false);
    };

    // RENDER
    return (
        <div className={classes.actionPlanWrapper} data-testid="action_plan">
            <div className={classes.tableWrapper}>
                <div className={app.sectionToolBar}>
                    <h2 className={app.sectionHeading}>Your action details</h2>
                    <Button
                        id="actionPlanPrintButton"
                        disabled={!participantActionPlans.length || isDisabled}
                        content="Generate PDF"
                        customClass="noButtonTopMargin"
                        onClick={() => setShowPdfPreview(true)}
                    />
                </div>

                {rows.length < 1 ? (
                    <LoadingSpinner content="No actions found" />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        loadResults={loadActionPlans}
                        passRowMetaDataUp={setRowMetaData}
                        apiParams={currentParticipant.id}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!openEditor && (
                    <Button
                        id="actionPlanCreateButton"
                        disabled={isDisabled}
                        content="Add an Action"
                        onClick={onCreateAction}
                        icon={true}
                    />
                )}
                {openEditor && formType === 'create' && (
                    <CreateActionPlan
                        onClose={onEditorClose}
                        roles={roles}
                        isDisabled={isDisabled}
                        isViewOnly={isViewOnly}
                    />
                )}
                {openEditor && formType === 'edit' && (
                    <EditActionPlan
                        onClose={onEditorClose}
                        roles={roles}
                        isDisabled={isDisabled}
                        isViewOnly={isViewOnly}
                        row={selectedRow}
                    />
                )}
            </div>
            {showPdfPreview && (
                <Dialog fullWidth={true} maxWidth={'md'} open={true}>
                    <DialogTitle id="id">Preview PDF</DialogTitle>
                    <GenerateActionPlanPDF
                        participant={currentParticipant}
                        barriers={currentParticipantBarriers}
                        actions={participantActionPlans}
                        templateContainerRef={contentTemplateContainerTemplateRef}
                        onClosePdfPreview={onClosePdfPreview}
                    />
                </Dialog>
            )}
        </div>
    );
};

ActionPlan.propTypes = {
    isViewOnly: PropTypes.bool
};

export default ActionPlan;
